import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { Badge } from './ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Save, Edit, Trash2, Copy, Eye, FolderIcon, Plus, X } from 'lucide-react'
import { toast } from 'react-hot-toast'
import TemplateService from '../services/templateService'
import RichTextEditor from './RichTextEditor'

const TemplateManager = ({ onClose, onTemplateSelect }) => {
  const [savedTemplates, setSavedTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')

  // Load saved templates from localStorage
  useEffect(() => {
    loadTemplates()
    
    // Subscribe to template changes from other components
    const unsubscribe = TemplateService.subscribe((templates) => {
      setSavedTemplates(templates)
    })
    
    return unsubscribe
  }, [])

  const loadTemplates = () => {
    try {
      const templates = TemplateService.getTemplates()
      setSavedTemplates(templates)
    } catch (error) {
      console.error('Error loading templates:', error)
      toast.error('Fehler beim Laden der Vorlagen')
    }
  }

  const saveTemplates = (templates) => {
    try {
      TemplateService.saveTemplates(templates)
      setSavedTemplates(templates)
      toast.success('Vorlagen gespeichert')
    } catch (error) {
      console.error('Error saving templates:', error)
      toast.error('Fehler beim Speichern')
    }
  }

  const deleteTemplate = (templateId) => {
    if (window.confirm('Vorlage wirklich löschen?')) {
      const updatedTemplates = savedTemplates.filter(t => t.id !== templateId)
      saveTemplates(updatedTemplates)
    }
  }

  const duplicateTemplate = (template) => {
    const duplicated = {
      ...template,
      id: Date.now().toString(),
      title: `${template.title} (Kopie)`,
      createdAt: new Date().toISOString()
    }
    const updatedTemplates = [...savedTemplates, duplicated]
    saveTemplates(updatedTemplates)
  }

  const editTemplate = (template) => {
    setEditingTemplate({ ...template })
    setIsEditing(true)
  }

  const useTemplate = (template) => {
    if (onTemplateSelect) {
      console.log('🔍 Using template for addition to sections:', template)
      onTemplateSelect(template)
      toast.success(`Vorlage "${template.title}" wurde zu den Angebot-Bereichen hinzugefügt`)
    } else {
      console.log('🔍 No onTemplateSelect callback available')
      toast.error('Template konnte nicht hinzugefügt werden')
    }
  }

  const createNewTemplate = () => {
    const newTemplate = {
      id: Date.now().toString(),
      title: 'Neue Vorlage',
      content: '',
      type: 'text',
      createdAt: new Date().toISOString()
    }
    setEditingTemplate(newTemplate)
    setIsEditing(true)
  }

  const saveNewTemplate = () => {
    if (!editingTemplate.title || !editingTemplate.content) {
      toast.error('Titel und Inhalt sind erforderlich')
      return
    }

    // Check if this is a new template (not in the list yet)
    const isNew = !savedTemplates.find(t => t.id === editingTemplate.id)
    
    let updatedTemplates
    if (isNew) {
      updatedTemplates = [...savedTemplates, editingTemplate]
    } else {
      updatedTemplates = savedTemplates.map(t => 
        t.id === editingTemplate.id ? editingTemplate : t
      )
    }
    
    saveTemplates(updatedTemplates)
    setIsEditing(false)
    setEditingTemplate(null)
  }

  const exportTemplates = () => {
    try {
      TemplateService.exportTemplates()
      toast.success('Vorlagen exportiert')
    } catch (error) {
      toast.error('Export fehlgeschlagen')
    }
  }

  const importTemplates = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    try {
      const count = await TemplateService.importTemplates(file, true)
      loadTemplates() // Reload to reflect changes
      toast.success(`${count} Vorlage(n) importiert`)
    } catch (error) {
      toast.error('Import fehlgeschlagen - ungültiges Format')
    }
  }

  // Filter templates
  const filteredTemplates = savedTemplates.filter(template => {
    const matchesSearch = template.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         template.content.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesType = filterType === 'all' || template.type === filterType
    return matchesSearch && matchesType
  })

  const getSectionIcon = (type) => {
    const icons = {
      'text': '📝',
      'terms': '📋', 
      'delivery': '🚚',
      'warranty': '🛡️',
      'process': '⚙️',
      'included': '✅',
      'notes': '💬',
      'closing': '🤝'
    }
    return icons[type] || '📄'
  }

  const getTypeColor = (type) => {
    const colors = {
      'text': 'bg-slate-100 text-slate-800',
      'terms': 'bg-blue-100 text-blue-800',
      'delivery': 'bg-orange-100 text-orange-800', 
      'warranty': 'bg-green-100 text-green-800',
      'process': 'bg-purple-100 text-purple-800',
      'included': 'bg-emerald-100 text-emerald-800',
      'notes': 'bg-yellow-100 text-yellow-800',
      'closing': 'bg-pink-100 text-pink-800'
    }
    return colors[type] || 'bg-gray-100 text-gray-800'
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center text-slate-800">
              <FolderIcon className="h-5 w-5 mr-2" />
              Vorlagen-Verwaltung
            </CardTitle>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={createNewTemplate}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Neue Vorlage
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          {savedTemplates.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <div className="text-4xl mb-4">📚</div>
              <p>Noch keine Vorlagen gespeichert</p>
              <p className="text-sm mt-2">Klicken Sie auf "Neue Vorlage" um zu beginnen</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Search and Filter */}
              <div className="flex gap-3 mb-4">
                <Input
                  placeholder="Vorlagen durchsuchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Typen</SelectItem>
                    <SelectItem value="text">📝 Text</SelectItem>
                    <SelectItem value="terms">📋 Bedingungen</SelectItem>
                    <SelectItem value="delivery">🚚 Lieferzeit</SelectItem>
                    <SelectItem value="warranty">🛡️ Gewährleistung</SelectItem>
                    <SelectItem value="process">⚙️ Prozess</SelectItem>
                    <SelectItem value="included">✅ Inkludiert</SelectItem>
                    <SelectItem value="notes">💬 Hinweise</SelectItem>
                    <SelectItem value="closing">🤝 Schlusswort</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Export/Import Buttons */}
              <div className="flex gap-2 mb-4">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={exportTemplates}
                  className="flex items-center gap-2"
                >
                  <Save className="h-3 w-3" />
                  Exportieren
                </Button>
                <label>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex items-center gap-2"
                    onClick={() => document.getElementById('import-input').click()}
                  >
                    <FolderIcon className="h-3 w-3" />
                    Importieren
                  </Button>
                  <input
                    id="import-input"
                    type="file"
                    accept=".json"
                    onChange={importTemplates}
                    className="hidden"
                  />
                </label>
              </div>
              
              <div className="text-sm text-slate-600 mb-4">
                {filteredTemplates.length} von {savedTemplates.length} Vorlage{savedTemplates.length !== 1 ? 'n' : ''}
                {searchTerm && ` (gefiltert)`}
              </div>
              
              {filteredTemplates.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <p>Keine Vorlagen gefunden</p>
                  <p className="text-sm">Versuchen Sie einen anderen Suchbegriff</p>
                </div>
              ) : (
                filteredTemplates.map((template) => {
                  return (
                <div key={template.id} className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h4 className="font-semibold text-slate-800">
                          {getSectionIcon(template.type)} {template.title}
                        </h4>
                        <Badge className={getTypeColor(template.type)}>
                          {template.type}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-slate-600 mb-3 bg-slate-50 p-3 rounded max-h-20 overflow-hidden">
                        {template.content.substring(0, 150)}...
                      </div>
                      
                      <div className="text-xs text-slate-500">
                        Erstellt: {new Date(template.createdAt).toLocaleDateString('de-DE')}
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2 ml-4">
                      <Button
                        size="sm"
                        onClick={() => useTemplate(template)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Verwenden
                      </Button>
                      
                      <div className="flex space-x-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedTemplate(template)}
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => editTemplate(template)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => duplicateTemplate(template)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteTemplate(template.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                  )
                })
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Template Preview Modal */}
      {selectedTemplate && (
        <Dialog open={!!selectedTemplate} onOpenChange={() => setSelectedTemplate(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {getSectionIcon(selectedTemplate.type)} {selectedTemplate.title}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Badge className={getTypeColor(selectedTemplate.type)}>
                  {selectedTemplate.type}
                </Badge>
              </div>
              
              <div className="bg-slate-50 p-4 rounded-lg border max-h-96 overflow-y-auto">
                <div 
                  className="text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: selectedTemplate.content
                      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/g, '<em>$1</em>')
                      .replace(/• /g, '<span style="color: #2563eb;">•</span> ')
                      .replace(/✓ /g, '<span style="color: #16a34a; font-weight: bold;">✓</span> ')
                      .replace(/\n/g, '<br />')
                  }}
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setSelectedTemplate(null)}>
                  Schließen
                </Button>
                <Button onClick={() => useTemplate(selectedTemplate)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Verwenden
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Template Edit Modal */}
      {isEditing && editingTemplate && (
        <Dialog open={isEditing} onOpenChange={() => setIsEditing(false)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Vorlage bearbeiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Titel</label>
                  <Input
                    value={editingTemplate.title}
                    onChange={(e) => setEditingTemplate({...editingTemplate, title: e.target.value})}
                    placeholder="Vorlagen-Titel"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Typ</label>
                  <Select
                    value={editingTemplate.type}
                    onValueChange={(value) => setEditingTemplate({...editingTemplate, type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">📝 Allgemeiner Text</SelectItem>
                      <SelectItem value="terms">📋 Zahlungsbedingungen</SelectItem>
                      <SelectItem value="delivery">🚚 Lieferzeit</SelectItem>
                      <SelectItem value="warranty">🛡️ Gewährleistung</SelectItem>
                      <SelectItem value="process">⚙️ Projektablauf</SelectItem>
                      <SelectItem value="included">✅ Inkludierte Leistungen</SelectItem>
                      <SelectItem value="notes">💬 Hinweise</SelectItem>
                      <SelectItem value="closing">🤝 Schlusswort</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Inhalt *
                </label>
                <RichTextEditor
                  value={editingTemplate.content || ''}
                  onChange={(content) => setEditingTemplate({...editingTemplate, content})}
                  placeholder="Inhalt der Vorlage..."
                  height={200}
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsEditing(false)}>
                  Abbrechen
                </Button>
                <Button onClick={saveNewTemplate} className="bg-blue-600 hover:bg-blue-700">
                  <Save className="h-4 w-4 mr-2" />
                  Speichern
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  )
}

export default TemplateManager